import { useNavigate } from "react-router-dom";
import {
  usePaths,
  useStartPath,
} from "@/api";
import {
  Badge,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Paper,
  Stack,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { ClockIcon, FootprintsIcon, TargetIcon } from "@phosphor-icons/react";
import { Image } from "@mantine/core";


export default function NewPaths() {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const navigate = useNavigate();

  const { data: pathsDataList } = usePaths({
    status: "new",
  });
  const { mutateAsync: startPath } =
    useStartPath();
  const startPathHandler = (path_id: number) => {
    startPath({ id: path_id }).then(() => navigate("/app/path/in-progress")).catch((err) => console.error(err));
  };
  return (
    <Stack gap="md">
      {pathsDataList?.results?.map((item) => (
        <Card
          key={item?.id}
          withBorder
          bg={
            colorScheme === "dark"
              ? theme.colors.neutral[9]
              : theme.colors.orange[0]
          }
          radius="md"
          p={{ base: "sm", sm: "md" }}
        >
          <Stack gap="sm">
            {/* Header Section */}
            <Group justify="space-between" align="start" wrap="wrap">
              <Stack gap="xs" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                <Group gap="xs" align="center" wrap="nowrap">
                  <Image
                    src={
                      item?.status === "completed"
                        ? "/assets/completed.png"
                        : item?.status === "in_progress"
                          ? "/assets/in_progress.png"
                          : "/assets/not-started-logo.png"
                    }
                    h={24}
                    w={24}
                    fit="contain"
                  />
                  <Text
                    c={
                      item?.status === "completed"
                        ? colorScheme === "dark" ? "#4B9BE5" : theme.colors.indigo[8]
                        : item?.status === "in_progress" || item?.status === "new"
                          ? colorScheme === "dark" ? "#FA7D87" : theme.colors.red[8]
                          : colorScheme === "dark" ? "#FA7D87" : theme.colors.red[8]
                    }
                    variant="subtle"
                    fw={500}
                    size="sm"
                    style={{
                      whiteSpace: "nowrap",
                      fontSize: window.innerWidth < 768 ? "14px" : "16px"
                    }}
                  >
                    {item?.status === "completed" ? "Completed" : 
                     item?.status === "in_progress" ? "In Progress" : 
                     item?.status === "new" ? "Not started" : 
                     item?.status || "Not started"}
                  </Text>
                </Group>
                
                <Text 
                  fw={500}
                  mt="xs"
                  style={{
                    fontSize: "18px",
                    color: colorScheme === "dark" ? "white" : "#393939",
                    lineHeight: "120%",
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {item?.name ?? ""}
                </Text>
                
                {item?.description && (
                  <Text 
                    size="sm" 
                    c={colorScheme === "dark" ? theme.colors.neutral[3] : theme.colors.neutral[7]}
                    style={{ 
                      lineHeight: 1.5,
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      marginTop: "4px"
                    }}
                  >
                    {item?.description}
                  </Text>
                )}
              </Stack>

              <Group gap="xs" wrap="wrap" justify="flex-end">
                {item?.tags && Array.isArray(item.tags) && item.tags.length > 0 && 
                  item.tags.map((tag: string) => {
                    const tagLower = tag.toLowerCase();
                    let bgColor = "#e3f2fd"; // Light blue for Lifestyle
                    let textColor = "#1976d2"; // Dark blue
                    
                    if (tagLower === "prevention") {
                      bgColor = "#f3e5f5"; // Light purple
                      textColor = "#7b1fa2"; // Dark purple
                    } else if (tagLower === "nutrition") {
                      bgColor = "#e0f2f1"; // Light teal
                      textColor = "#00695c"; // Dark teal
                    } else if (tagLower === "lifestyle") {
                      bgColor = "#e3f2fd"; // Light blue
                      textColor = "#1976d2"; // Dark blue
                    }
                    
                    return (
                      <Badge
                        key={tag}
                        radius="xl"
                        size="sm"
                        style={{
                          backgroundColor: bgColor,
                          color: textColor,
                          border: "none",
                          fontWeight: 500,
                          textTransform: "none",
                          padding: "4px 12px",
                        }}
                      >
                        {tag}
                      </Badge>
                    );
                  })
                }
              </Group>
            </Group>

            {/* Info and Button Section */}
            <Stack gap="sm">
              {/* Mobile: Stack vertically, Desktop: Side by side */}
              <Flex 
                gap="md" 
                align="center" 
                justify="space-between" 
                wrap="wrap"
                direction={{ base: "column", sm: "row" }}
              >
                {/* Info Paper */}
                <Paper
                  withBorder
                  radius="md"
                  p="xs"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    minWidth: window.innerWidth < 768 ? "100%" : 300,
                    maxWidth: window.innerWidth < 768 ? "100%" : 500,
                  }}
                  bg={
                    colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : "#f8f9fa"
                  }
                >
                  <Group 
                    gap={window.innerWidth < 768 ? 4 : 8} 
                    wrap="wrap" 
                    justify="center"
                    style={{ width: "100%" }}
                  >
                    <Group gap={6} style={{ minWidth: "fit-content" }}>
                      <ClockIcon size={window.innerWidth < 768 ? 16 : 18} color={colorScheme === "dark" ? "#247ED3" : theme.colors.blue[8]} />
                      <Text 
                        size={window.innerWidth < 768 ? "xs" : "sm"} 
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {item?.avg_duration} min/session
                      </Text>
                    </Group>
                    <Divider orientation="vertical" mx="sm" />
                    <Group gap={6} style={{ minWidth: "fit-content" }}>
                      <FootprintsIcon size={window.innerWidth < 768 ? 16 : 18} color={theme.colors.green[9]} />
                      <Text 
                        size={window.innerWidth < 768 ? "xs" : "sm"} 
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {item?.path_sessions?.length || 0} Waypoints
                      </Text>
                    </Group>
                    <Divider orientation="vertical" mx="sm" />
                    <Group gap={6} style={{ minWidth: "fit-content" }}>
                      <TargetIcon size={window.innerWidth < 768 ? 16 : 18} color={theme.colors.orange[7]} />
                      <Text 
                        size={window.innerWidth < 768 ? "xs" : "sm"} 
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {item?.level}
                      </Text>
                    </Group>
                  </Group>
                </Paper>

                {/* Button */}
                <Button
                  onClick={() => startPathHandler(item?.id)}
                  variant="transparent"
                  size={window.innerWidth < 768 ? "sm" : "md"}
                  c={
                    colorScheme === "dark"
                      ? "#4B9BE5"
                      : theme.colors.indigo[9]
                  }
                  w={{ base: "100%", sm: "auto" }}
                >
                  Start Path
                </Button>
              </Flex>
            </Stack>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
